// The field limits of app.lead and app.lead_note; see platform/api/migrations/versions/0020_leads.py.
export const NAME_MAX = 200;
export const ADDRESS_MAX = 300;
export const PHONE_MAX = 40;
export const SHORT_MAX = 200;
export const NOTE_MAX = 2000;
export const MINUTES_MAX = 600;

export const STAGES = ["lead", "qualified", "proposal", "won", "lost"] as const;
export type Stage = (typeof STAGES)[number];

export const STAGE_TEXT: Record<Stage, string> = {
  lead: "Lead",
  qualified: "Qualified",
  proposal: "Proposal",
  won: "Won",
  lost: "Lost",
};

export const SOURCES = ["hand", "call", "event", "referral", "import", "photo"] as const;
export type Source = (typeof SOURCES)[number];

export const SOURCE_TEXT: Record<Source, string> = {
  hand: "Found by sales",
  call: "Call to the office",
  event: "Event (fair, carnival)",
  referral: "Referral",
  import: "Import",
  photo: "Driver photo",
};

// The sources that a user picks; an import and a driver photo come from their own flows.
export const PICKED_SOURCES: readonly Source[] = ["hand", "call", "event", "referral"];

export const LOST_REASONS = ["price", "competitor", "no_volume", "closed", "other"] as const;
export type LostReason = (typeof LOST_REASONS)[number];

export const LOST_TEXT: Record<LostReason, string> = {
  price: "Price",
  competitor: "Competitor contract",
  no_volume: "No volume",
  closed: "Closed business",
  other: "Other",
};

export const NOTE_KINDS = ["visit", "call", "email", "message"] as const;
export type NoteKind = (typeof NOTE_KINDS)[number];

export const NOTE_KIND_TEXT: Record<NoteKind, string> = {
  visit: "Visit",
  call: "Call",
  email: "Email",
  message: "Left a message",
};

export type LeadView = "inbox" | "mine" | "all";

const OPEN: readonly Stage[] = ["lead", "qualified", "proposal"];

/** The stages that a lead can move to now (rule Q-2). Only its deal makes a lead won (Q-11). */
export function nextStages(stage: Stage): Stage[] {
  if (stage === "won") return [];
  if (stage === "lost") return [...OPEN];
  return STAGES.filter((to) => to !== stage && to !== "won");
}

/** The tab of the Leads page. A user who cannot own a lead has no "Mine", so it starts on all leads. */
export function defaultLeadView(search: string, canOwn: boolean | undefined): LeadView {
  const asked = new URLSearchParams(search).get("view");
  if (asked === "inbox" || asked === "all" || (asked === "mine" && canOwn !== false)) return asked;
  return canOwn === false ? "all" : "mine";
}

export type LeadField = "name" | "address" | "phone" | "note";

export interface LeadProblem {
  field: LeadField;
  text: string;
}

/** The API refuses a lead phone with fewer digits; see PHONE_DIGITS in platform/api/app/leads.py. */
export const PHONE_DIGITS = 10;
export const PHONE_SHORT = "Enter a phone number with 10 digits or more, for example 717 555 0142.";
const phoneDigits = (phone: string) => (phone.match(/[0-9]/g) ?? []).length;

/** The first problem in the new lead form, with the field that has it. */
export function leadProblem(name: string, address: string, phone: string, note: string): LeadProblem | null {
  if (!name.trim()) return { field: "name", text: "Enter the name of the place." };
  if (name.trim().length > NAME_MAX) return { field: "name", text: `The name can have ${NAME_MAX} characters at most.` };
  if (address.trim().length > ADDRESS_MAX) return { field: "address", text: `The address can have ${ADDRESS_MAX} characters at most.` };
  if (phone.trim().length > PHONE_MAX) return { field: "phone", text: `The phone can have ${PHONE_MAX} characters at most.` };
  if (phone.trim() && phoneDigits(phone) < PHONE_DIGITS) return { field: "phone", text: PHONE_SHORT };
  if (note.trim().length > NOTE_MAX) return { field: "note", text: `The note can have ${NOTE_MAX} characters at most.` };
  return null;
}

/** The warning before the save of a lead that can be a duplicate. */
export function duplicateText(count: number): string {
  const what = count === 1 ? "1 lead or account has" : `${count} leads or accounts have`;
  return `${what} the same name, address or phone. Open a match below to check it, or select "Add the lead anyway".`;
}

/** The problem of a sales note: no text, too long, or minutes out of range. */
export function noteError(note: string, minutes: string): string {
  if (!note.trim()) return "Write the note.";
  if (note.trim().length > NOTE_MAX) return `The note can have ${NOTE_MAX} characters at most.`;
  if (minutes.trim() === "") return "";
  const value = Number(minutes);
  if (!Number.isInteger(value) || value < 0 || value > MINUTES_MAX) return `Minutes must be a whole number from 0 to ${MINUTES_MAX}.`;
  return "";
}

/** "Lost: Competitor contract · three-year deal", or the stage alone. */
export function stageText(stage: Stage, reason: LostReason | null, note: string): string {
  if (stage !== "lost" || !reason) return STAGE_TEXT[stage];
  return `${STAGE_TEXT.lost}: ${LOST_TEXT[reason]}${note ? ` · ${note}` : ""}`;
}

// The call text stays in this tab only, so it is not in the URL or the browser history.
const CALL_NOTE_KEY = "lead-from-call";

/** The new lead form that a call opens: `?new=call` on the Leads page. */
export function callLeadPath(base: string): string {
  return `${base}/leads?new=call`;
}

export function keepCallNote(text: string): void {
  try {
    sessionStorage.setItem(CALL_NOTE_KEY, text);
  } catch {
    // Without storage the form opens with an empty note.
  }
}

export function storedCallNote(): string | null {
  try {
    return sessionStorage.getItem(CALL_NOTE_KEY);
  } catch {
    return null;
  }
}

export function forgetCallNote(): void {
  try {
    sessionStorage.removeItem(CALL_NOTE_KEY);
  } catch {
    // Nothing to remove.
  }
}

export function readNewLead(search: string, stored: string | null): { source: Source; note: string } | null {
  if (new URLSearchParams(search).get("new") !== "call") return null;
  return { source: "call", note: (stored ?? "").slice(0, NOTE_MAX) };
}

export function readStage(search: string): Stage | null {
  const asked = new URLSearchParams(search).get("stage");
  return STAGES.includes(asked as Stage) ? (asked as Stage) : null;
}

// The file limit of an import; see IMPORT_MAX_BYTES in platform/api/app/leads.py.
export const IMPORT_MAX_BYTES = 5_000_000;

/** "12 rows are ready." or "3 of 12 rows have errors. Correct the file, then check it again." */
export function importSummary(rows: number, bad: number, ignored: string[]): string {
  const extra = ignored.length ? ` The import ignores the columns ${ignored.join(", ")}.` : "";
  if (!bad) return `${rows} ${rows === 1 ? "row is" : "rows are"} ready to import.${extra}`;
  return `${bad} of ${rows} rows ${bad === 1 ? "has" : "have"} errors. Correct the file, then check it again.${extra}`;
}
